import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { ButtonClubbi, ChevronDownIcon, CopyIcon, Tooltip, themeClubbi } from 'clubbi-ui'

import { useAppSelector } from '../../../common/hooks'
import { ProductInput, salesFlyerActions } from '../../../store/slices/salesFlyer'

import {
  StyledOpenButton,
  StyledDescription,
  StyledHeaderContainer,
  StyledTitle,
  StyledContainer,
  StyledInputsContainer,
  StyledInputLabel,
  StyledInputMerchatCode,
  StyledInputEans,
  StyledInfo,
  StyledCopyButton,
  StyledInfoAndButtonContainer,
  StyledDateContainer,
  StyledCheckbox,
} from './styles/DetailsFlyerInput.style'
import { extractProductData, parseEntries } from './helpers'

interface DetailsFlyerInputProps {
  getFlyerProducts: () => void
  disabled: boolean
  products: ProductInput[]
}

export const DetailsFlyerInput = ({
  getFlyerProducts,
  disabled,
  products,
}: DetailsFlyerInputProps) => {
  const dispatch = useDispatch()
  const { merchantCodeInput, eans, includeDate } = useAppSelector((state) => state.salesFlyer)
  const [open, setOpen] = useState<boolean>(true)
  const [copy, setCopy] = useState<boolean>(false)
  const [productsText, setProductsText] = useState(() => {
    if (products && products.length > 0) {
      return products.map((p: ProductInput) => `${p.ean};${p.description};${p.price}`).join('\n')
    }
    return ''
  })

  const setContainerStatus = () => {
    setOpen(!open)
  }

  const changeMerchantCodeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const codeInput = event.target.value
    if (codeInput.length <= 5)
      dispatch(salesFlyerActions.setMerchantCodeInput(codeInput.toUpperCase()))
  }

  const copyEans = async () => {
    navigator.clipboard.writeText(eans.join('\n'))
    setCopy(true)

    setTimeout(() => {
      setCopy(false)
    }, 800)
  }

  const setIncludeDate = () => {
    dispatch(salesFlyerActions.setIncludeDate(!includeDate))
  }

  const handleProductsTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = event.target.value
    setProductsText(newText)

    const entries = parseEntries(newText)

    const finalEans: string[] = []
    const finalPrices: Record<string, string> = {}
    const finalProducts: ProductInput[] = []

    for (const entry of entries) {
      const productData = extractProductData(entry)

      if (productData) {
        finalProducts.push(productData)
        finalEans.push(productData.ean)
        if (productData.price) {
          finalPrices[productData.ean] = productData.price
        }
      }
    }

    dispatch(salesFlyerActions.setEans(finalEans))
    dispatch(salesFlyerActions.setEditedPrices(finalPrices))
    dispatch(salesFlyerActions.setProducts(finalProducts))
  }

  return (
    <StyledContainer>
      <StyledHeaderContainer>
        <StyledOpenButton onClick={setContainerStatus}>
          <StyledDescription>
            <StyledTitle>1. Detalhes</StyledTitle>(Código e Lista de produtos)
          </StyledDescription>
          <ChevronDownIcon
            color={themeClubbi.palette.brandBlack.main}
            rotate={open}
            bold={true}
            fontSize={25}
          />
        </StyledOpenButton>
      </StyledHeaderContainer>
      {open && (
        <StyledInputsContainer>
          <StyledInputLabel>
            Código do cliente
            <StyledInputMerchatCode
              value={merchantCodeInput}
              onChange={changeMerchantCodeInput}
              placeholder="QTKAU"
              type="text"
            />
          </StyledInputLabel>
          <StyledInputLabel>
            Lista de produtos
            <StyledInfoAndButtonContainer>
              <StyledInfo>
                Digite um EAN por linha Ex: 7891234567890 Ou formato: EAN;Descrição;Preço
              </StyledInfo>
              <Tooltip title="Lista de EANs copiada!" placement="bottom-end" open={copy}>
                <StyledCopyButton onClick={copyEans}>
                  <CopyIcon
                    color={themeClubbi.palette.brandPurple[60]}
                    size={themeClubbi.typography.fontSizeM}
                  />
                </StyledCopyButton>
              </Tooltip>
            </StyledInfoAndButtonContainer>
            <StyledInputEans value={productsText} onChange={handleProductsTextChange} />
          </StyledInputLabel>
          <StyledDateContainer>
            <StyledCheckbox type="checkbox" checked={includeDate} onChange={setIncludeDate} />
            Incluir data no encarte
          </StyledDateContainer>
          <ButtonClubbi
            data-cy="profile-login-button"
            typeButton="filled"
            size="small"
            label="Gerar encarte"
            onClick={getFlyerProducts}
            disabled={disabled}
            data-testid="generator-button"
          />
        </StyledInputsContainer>
      )}
    </StyledContainer>
  )
}
